/**
 * useKeyboardShortcuts Hook
 * Listens for keyboard events and triggers configured shortcut actions
 * 
 * Usage:
 *   const { shortcutsEnabled, setShortcutsEnabled, activeShortcuts } = useKeyboardShortcuts('trading-view', actionHandlers);
 * 
 * actionHandlers is an object mapping action_id to handler functions:
 *   { buy_ce: () => handleQuickOrder(ce, 'BUY'), sell_ce: () => ..., order_type_limit: () => setOrderType('LIMIT'), ... }
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { normalizeKey } from '../config/shortcutConfig';

const useKeyboardShortcuts = (pageId, actionHandlers = {}) => {
  // Always start disabled - user must manually enable each visit
  const [shortcutsEnabled, setShortcutsEnabled] = useState(false);
  const [shortcuts, setShortcuts] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [lastTriggered, setLastTriggered] = useState(null);
  const handlersRef = useRef(actionHandlers);

  // Keep handlers ref up to date
  useEffect(() => {
    handlersRef.current = actionHandlers;
  }, [actionHandlers]);

  // Fetch shortcuts for this page
  const fetchShortcuts = useCallback(async () => {
    try {
      const response = await api.get(`/keyboard-shortcuts?page_id=${pageId}`);
      if (response.data.success) {
        setShortcuts(response.data.data);
        setLoadError(null);
      }
    } catch (err) {
      console.error(`Failed to load shortcuts for ${pageId}:`, err);
      setLoadError(err.message);
    }
  }, [pageId]);

  useEffect(() => {
    fetchShortcuts();
  }, [fetchShortcuts]);

  // Keyboard event listener
  useEffect(() => {
    if (!shortcutsEnabled || shortcuts.length === 0) return;

    const handleKeyDown = (event) => {
      // Don't trigger if user is typing in an input/textarea/select
      const tag = event.target.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (event.target.isContentEditable) return;

      const pressedKey = normalizeKey(event);
      const hasCtrl = event.ctrlKey || event.metaKey;
      const hasShift = event.shiftKey;
      const hasAlt = event.altKey;

      // Find matching shortcut
      for (const shortcut of shortcuts) {
        let match = false;

        if (shortcut.key_type === 'single') {
          // Single key: must match key, no modifiers held (except for special keys)
          if (pressedKey === shortcut.shortcut_key && !hasCtrl && !hasAlt) {
            // Allow shift for letters (Shift+B = B)
            match = true;
          }
        } else if (shortcut.key_type === 'multi') {
          // Multi key: modifier + key must match
          const mod = shortcut.modifier_key;
          let modifierMatch = false;

          // Check if modifier matches
          if ((mod === 'Ctrl' || mod === 'CtrlRight') && hasCtrl && !hasShift && !hasAlt) modifierMatch = true;
          else if ((mod === 'Shift' || mod === 'ShiftRight') && hasShift && !hasCtrl && !hasAlt) modifierMatch = true;
          else if ((mod === 'Alt' || mod === 'AltRight') && hasAlt && !hasCtrl && !hasShift) modifierMatch = true;
          // F-key as modifier: F-key must be the "modifier" and the event key is the actual key
          // This is harder since F-keys are standalone keys. For F-key modifiers,
          // we treat it as: the shortcut_key IS the F-key itself (single shortcut effectively)
          // Actually for multi with F-key as modifier, we need a different approach.
          // Let's treat F-key modifiers as: F-key pressed = triggers the shortcut
          else if (mod && mod.startsWith('F') && pressedKey === mod) {
            // F-key modifier with another key: check if the shortcut_key matches too
            // Since we can't truly hold F-keys as modifiers, treat F+key combos as sequential
            // For now, just match the F-key press alone for F-key "modifiers"
            modifierMatch = true;
          }

          if (modifierMatch && pressedKey === shortcut.shortcut_key) {
            match = true;
          }
          // Special case: if modifier is F-key and shortcut_key is different 
          // we can't truly do F-key combos, so skip
        }

        if (match) {
          event.preventDefault();
          event.stopPropagation();

          const handler = handlersRef.current[shortcut.action_id];
          if (handler) {
            handler();
            setLastTriggered({
              action: shortcut.action_label,
              key: shortcut.modifier_key
                ? `${shortcut.modifier_key} + ${shortcut.shortcut_key}`
                : shortcut.shortcut_key,
              timestamp: Date.now()
            });
            console.log(`⌨️ Shortcut triggered: ${shortcut.action_label} (${shortcut.modifier_key ? shortcut.modifier_key + '+' : ''}${shortcut.shortcut_key})`);
          } else {
            console.warn(`⌨️ No handler for action: ${shortcut.action_id}`);
          }
          return; // Only trigger first match
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [shortcutsEnabled, shortcuts]);

  return {
    shortcutsEnabled,
    setShortcutsEnabled,
    shortcuts,
    lastTriggered,
    loadError,
    refreshShortcuts: fetchShortcuts,
  };
};

export default useKeyboardShortcuts;
